import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LayoutUtilsService } from 'src/app/core/_base/crud';
import { AuthService } from 'src/app/core/auth/service/auth.service';
import { BaseFormComponent } from 'src/app/shared/crud/base-form.component';
import { checkPasswords } from 'src/app/shared/validators/check-passwords.validator';
import { cnpjValidator } from 'src/app/shared/validators/cnpj.validator';
import { cpfValidator } from 'src/app/shared/validators/cpf.validator';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent
  extends BaseFormComponent
  implements OnInit, OnDestroy
{
  showPass = false;
  isCnpjUser = false;
  isCpfUser = true; // Por padrão será pessoa física
  isSearchingCnpj = false;
  cnpjCompanyName = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    protected router: Router,
    private authService: AuthService,
    protected cdr: ChangeDetectorRef,
    protected layoutUtilsService: LayoutUtilsService
  ) {
    super();
  }

  ngOnInit(): void {
    super.initForm();
    // Inicializar estado baseado no valor padrão do formulário
    this.onUserTypeChange('person');
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  createForm() {
    this.form = this.fb.group(
      {
        userType: ['person', [Validators.required]], // 'person' ou 'company'
        name: [
          '',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(40),
          ],
        ],
        cnpj: [''], // Campo opcional, será obrigatório quando userType for 'company'
        cpf: [''], // Campo opcional, será obrigatório quando userType for 'person'
        companyName: [''], // Nome da empresa obtido via API
        email: ['', [Validators.required, Validators.email]],
        password: [
          '',
          [
            Validators.required,
            Validators.pattern('(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).{8,}'),
            Validators.maxLength(50),
          ],
        ],
        retypedPassword: [
          '',
          [
            Validators.required,
            Validators.pattern('(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).{8,}'),
            Validators.maxLength(50),
          ],
        ],
        termsAccepted: [
          false,
          [
            Validators.requiredTrue,
          ],
        ],
      },
      {
        validators: [checkPasswords],
      }
    );

    // Observar mudanças no tipo de usuário
    this.form.get('userType')?.valueChanges.subscribe((value: string) => {
      this.onUserTypeChange(value);
    });

    // Observar mudanças no CNPJ para buscar empresa
    this.form.get('cnpj')?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((cnpj: string) => {
        if (cnpj) {
          const control = { value: cnpj } as any;
          const validation = cnpjValidator(control);
          if (!validation) { // Se não tem erro, é válido
            this.searchCompanyByCnpj(cnpj);
          }
        } else {
          // Limpar apenas o nome encontrado automaticamente, não o campo digitado
          this.cnpjCompanyName = '';
        }
      });
  }

  onUserTypeChange(userType: string): void {
    this.isCnpjUser = userType === 'company';
    this.isCpfUser = userType === 'person';

    if (this.isCnpjUser) {
      // Adicionar validação obrigatória ao CNPJ
      this.form.get('cnpj')?.setValidators([Validators.required, cnpjValidator]);
      this.form.get('companyName')?.setValidators([Validators.required]);
      // Remover validação do CPF
      this.form.get('cpf')?.clearValidators();
      this.form.patchValue({ cpf: '' });
    } else {
      // Adicionar validação obrigatória ao CPF
      this.form.get('cpf')?.setValidators([Validators.required, cpfValidator]);
      // Remover validações do CNPJ e nome da empresa
      this.form.get('cnpj')?.clearValidators();
      this.form.get('companyName')?.clearValidators();
      this.form.patchValue({ cnpj: '', companyName: '' });
      this.cnpjCompanyName = '';
    }

    this.form.get('cnpj')?.updateValueAndValidity();
    this.form.get('cpf')?.updateValueAndValidity();
    this.form.get('companyName')?.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  searchCompanyByCnpj(cnpj: string): void {
    if (!cnpj) return;

    // Validar CNPJ antes de buscar
    const control = { value: cnpj } as any;
    const validation = cnpjValidator(control);
    if (validation && validation.cnpjInvalid) {
      return;
    }

    this.isSearchingCnpj = true;
    this.cdr.detectChanges();

    this.authService.searchCompanyByCnpj(cnpj)
      .pipe(
        finalize(() => {
          this.isSearchingCnpj = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: any) => {
          if (response && response.name) {
            this.cnpjCompanyName = response.name;
            this.form.patchValue({ companyName: response.name });
            this.cnpjCompanyName = '';
            // Não limpar o campo se o usuário já tiver digitado algo
            // Apenas mostrar mensagem informativa
            // this.layoutUtilsService?.showToastNotification({
            //   title: 'CNPJ não encontrado automaticamente',
            //   text: 'Você pode digitar a razão social manualmente',
            //   icon: 'info',
            // });
          }
        },
        (error: any) => {
          this.cnpjCompanyName = '';

          // Log do erro para debug
          console.error('Erro ao buscar empresa por CNPJ:', error);

          // Não limpar o campo, apenas informar o erro
          // this.layoutUtilsService?.showToastNotification({
          //   title: 'Erro ao buscar empresa',
          //   text: 'Você pode digitar a razão social manualmente',
          //   icon: 'warning',
          // });
        }
      );
  }

  submit() {
    if (!this.validateForm()) {
      this.cdr.detectChanges();
      return;
    }

    // Validações adicionais para CNPJ
    if (this.isCnpjUser) {
      const cnpj = this.form.get('cnpj')?.value;
      if (!cnpj) {
        this.layoutUtilsService?.showToastNotification({
          title: 'CNPJ é obrigatório',
          icon: 'error',
        });
        return;
      }

      const control = { value: cnpj } as any;
      const validation = cnpjValidator(control);
      if (validation && validation.cnpjInvalid) {
        this.layoutUtilsService?.showToastNotification({
          title: 'CNPJ inválido',
          icon: 'error',
        });
        return;
      }

      const companyName = this.form.get('companyName')?.value;
      if (!companyName || companyName.trim() === '') {
        this.layoutUtilsService?.showToastNotification({
          title: 'Razão Social obrigatória',
          text: 'Por favor, digite a razão social da empresa',
          icon: 'error',
        });
        return;
      }
    }

    // Validações adicionais para CPF
    if (this.isCpfUser) {
      const cpf = this.form.get('cpf')?.value;
      if (!cpf) {
        this.layoutUtilsService?.showToastNotification({
          title: 'CPF é obrigatório',
          icon: 'error',
        });
        return;
      }

      const control = { value: cpf.replace(/[^\d]/g, '') } as any;
      const validation = cpfValidator(control);
      if (validation && validation.cpfInvalid) {
        this.layoutUtilsService?.showToastNotification({
          title: 'CPF inválido',
          icon: 'error',
        });
        return;
      }
    }

    this.loadingSubject.next(true);
    const formData = this.form.getRawValue() as any;

    // Preparar dados para envio
    const registrationData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      retypedPassword: formData.retypedPassword,
      userType: formData.userType,
      ...(this.isCnpjUser && {
        cnpj: formData.cnpj.replace(/[^\d]/g, ''), // Enviar CNPJ limpo
        companyName: formData.companyName.trim()
      }),
      ...(this.isCpfUser && {
        cpf: formData.cpf.replace(/[^\d]/g, '') // Enviar CPF limpo
      })
    };

    this.authService
      .register(registrationData)
      .pipe(
        finalize(() => {
          this.loadingSubject.next(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe(
        () => {
          const accountTypeMessage = this.isCnpjUser ? 'empresa' : 'pessoal';
          this.layoutUtilsService?.showActionNotification({
            title: 'Conta Criada!',
            html: `Sua conta ${accountTypeMessage} foi criada com sucesso!<br><br>
                      <small class="fs-8">Utilize o email e senha para acessar sua conta.</small>`,
            icon: 'success',
            heightAuto: false,
          });

          this.router.navigate(['/auth/login']);
        },
        (err) => {
          this.layoutUtilsService?.showToastNotification({
            title: 'Erro ao criar conta',
            icon: 'error',
          });
        }
      );
  }

  togglePass() {
    this.showPass = !this.showPass;
  }
}
